import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

import { SetCaptionsTool } from 'js/src/agent/tools/SetCaptionsTool';

export const createProblemSolutionVideoSkill: Skill = {
    name: SKILL_NAMES.createProblemSolutionVideo,
    description: "Problem-solution video — pain points paired one by one with how they're solved.",
    kind: 'video',
    surfaces: ['sketch'],
    dashboard: {
        title: 'Problem / solution',
        blurb: 'Name a problem, then show how you solve it.',
        icon: 'lightbulb',
        gradient: ['#9C5A68', '#A96C7B'],
    },
    get instructions(): string {
        return `# Create Problem-Solution Video

The tension-and-payoff format: recognizable problems, each answered by its
solution, narrated by an AI voiceover.

Load the \`${SKILL_NAMES.createVideo}\` skill too — everything there applies; this skill adds the
problem-solution formula, and its rules win where they conflict.

## Structure — problem/solution pairs, trending toward 3
- Build from problem/solution pairs. Three is the strong default to steer toward;
  use judgment if the material genuinely supports 2 well-developed problems or a
  clean 4th.
- Each pair covers the problem's words, then the solution's words, as separate
  overlays:
  1. **Problem overlay** (no media): states the problem plainly and concretely —
     something the viewer recognizes or feels. The absence of media is the point:
     a bare, stark statement creates a beat of tension.
  2. **Solution overlay** (media): the payoff — footage that concretely shows the
     resolution in action (the fix, the product, the outcome).
- Never span a problem and its solution in one overlay's word range, and never
  combine multiple problems. A longer statement can split across overlays at
  natural clause breaks.
- Use the user's list as-is (nudging toward 3); otherwise offer to identify the 3
  most relevant problems from their material and confirm before generating.

## Problem vs. solution split
- Problem overlays: text-only designs (no media asset). Solution overlays: designs
  with a media slot — check the asset list before picking.
- Never a caption-style design for either side.
- Vary designs across pairs — separately for problems and for solutions. The
  built-in no-media/media alternation is the video's rhythm.
- Optionally reinforce the shift with a small "The Problem" / "The Solution" label
  when the design has room — a nice-to-have, not a requirement.

## Script structure
- Target roughly 45 seconds of narration — about 15 seconds per pair. A
  recognizable pain point, then a clear resolution; if 3 pairs don't fit, tighten
  the language before cutting a pair.
- State each problem in the viewer's own words — don't undersell it, don't wallow.
  Move to the solution promptly; a problem never lingers past its one beat.
- Each solution must directly and specifically resolve its paired problem — no
  generic solutions that could answer anything.
- Open with a hook naming the topic ("Here's what's holding your mornings back —
  and how to fix it") as the title card on a "title" design; write the pairs back
  to back in order; close with a real outro — recap what's now solved plus a
  topic-specific next-step line on an "outro" design.
- **Captions are OFF for this format** — on-screen text does that job. Call
  ${SetCaptionsTool.toolName} with enabled: false right after generating the voiceover.

## Review
Load the \`${SKILL_NAMES.reviewVideo}\` skill before the review pass.
Additional checks:
- No overlay shows a problem and its solution at once.
- Tally the media/no-media split overlay by overlay: problems text-only, solutions
  with visible media.
- Each solution actually resolves its paired problem — fix any generic or
  mismatched pairing.
- Confirm the title-card open, outro close, captions off, and that both design
  rotations vary; every overlay breaks at a natural clause boundary.`;
    },
};
